import React, {
  useMemo, memo, useEffect, useRef, useCallback, useState, Suspense,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useQuery, useMutation } from '@apollo/client'
import { Map } from 'immutable'
import _get from 'lodash/get'
import { compose } from 'recompose'
import { getBoundActions } from '../../../../../actions/index'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import { StaticTextProvider, useTranslatedText } from '../../../../StaticText/StaticTextContext'
import {
  connect as connectPage,
  PAGE_SEO_TYPE_LOCATION,
} from '../../../../Page/connect'
import { usePostHogEvent } from '../../../../PostHog/index'
import type { CommunityLibraryTab } from '../../../../PostHog/index'
import { selectAuth } from '../../../../../services/auth/selectors'
import useCommunityEnabled from '../../../../../services/community/shared/useCommunityEnabled'
import useRequireAuth from '../../../../../services/community/shared/useRequireAuth'
import usePrimaryAppLanguage from '../../../../../services/community/shared/usePrimaryAppLanguage'
import useCollectionsData from '../../../../../services/community/identity/library/useCollectionsData'
import {
  MobileBreakpointProvider,
  COMMUNITY_BREAKPOINTS,
} from '../../../../../services/community/shared/MobileBreakpointContext'
import { FieldVisibility } from '../../../../../graphql-types'
import { GET_PROFILE_IN_PROGRESS_CAROUSEL, GET_CURRENT_PROFILE_LIGHT, UPDATE_PROFILE } from '../../../../../services/community/query'
import { GET_PURCHASES_LIST } from '../../../../../services/playlist/query'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../../../../services/apollo-graphql/index'
import { CONTENT_IMAGE_LIST } from '../../../../../services/content/index'
import {
  URL_PLAYLIST_BONUS_VIDEOS,
  URL_PLAYLIST_BONUS_EBOOKS,
  URL_PLAYLIST_PURCHASES_VIDEOS,
  URL_PLAYLIST_PURCHASES_EBOOKS,
  URL_PLAYLIST_WATCH_HISTORY,
} from '../../../../../services/url/constants'
import { parse as parseQuery } from '../../../../../services/query-string/index'
import scrollToPlaylistSection from '../../shared/scrollToPlaylistSection'
import { getUserPurchases } from '../../../../MyLibraryPage/utils'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../../../../Loading/index'
import withCommunityErrorBoundary from '../../../shared/ErrorHandling/withCommunityErrorBoundary/index'
import CommunityMyLibraryNav from '../CommunityMyLibraryNav/index'
import PlaylistTabContainer from '../PlaylistTabs/PlaylistTabContainer/index'
import { TABS, type CollectionsTab } from '../PlaylistTabs/index'
import MyLibraryPage from '../../../../MyLibraryPage/MyLibraryPage'
import MyLibraryPurchasesVideos from '../../../../MyLibraryPage/MyLibraryPurchasesVideos'
import MyLibraryPurchasesEBooks from '../../../../MyLibraryPage/MyLibraryPurchasesEBooks'

import type {
  GetProfileInProgressCarouselResponse,
  GetCurrentProfileLightResponse,
  StaticTextMap,
  UpdateProfileResponse,
  ActivityVisibilityField,
  ActivityVisibilityValue,
} from '../../../../../services/community/types'

// First-visit announcement modal — only renders for logged-in users who have
// never dismissed it.  Lazy-loaded because returning users (the steady-state
// case) should not pay the parse cost of the modal's JS or its three device
// PNGs on every library-page visit.
const CommunityMyLibraryWelcome = React.lazy(
  () => import('../CommunityMyLibraryWelcome/index'),
)

export interface CommunityMyLibraryPageProps {
  history: { push: (path: string) => void }
  location: { pathname?: string; search?: string }
  staticText: StaticTextMap
}

function CommunityMyLibraryPageInner ({
  history,
  location,
  staticText,
}: CommunityMyLibraryPageProps) {
  // ── Feature gate — fall back to old library when community is off ──
  const { enabled: communityEnabled } = useCommunityEnabled()

  // ── Auth guard ──────────────────────────────────────────────────────
  const { isLoggedIn } = useRequireAuth()

  const auth = useSelector(selectAuth)
  const jwt = auth.get('jwt') as string
  const uid = auth.get('uid') as string
  const authUuid = auth.get('uuid') as string
  const dispatch = useDispatch()
  // `getBoundActions` returns a fresh object reference on every call.  Memoize
  // so any `useCallback` that closes over `actions` keeps a stable identity
  // across renders.
  const actions = useMemo(() => getBoundActions(dispatch), [dispatch])
  const language = usePrimaryAppLanguage()

  // ── Welcome overlay ─────────────────────────────────────────────────
  // Show-once announcement modal. Persisted in feature-tracking via the
  // `communityMyLibraryWelcomeMessage` key (default null = unseen).
  // Mirrors CommunityProfileSettingsPage's pattern; see that file for
  // the canonical comments on the two-phase dismiss.
  //
  // Coerce to a boolean at the selector boundary so this hook only triggers a
  // re-render when the *truthiness* of the flag changes, not on every
  // featureTracking dispatch that happens to touch other keys.  The reducer
  // (`feature-tracking/reducers.js:36`) merges into `data`, so writing
  // `{ communityMyLibraryWelcomeMessage: true }` is safe — other tracking
  // flags survive.
  const hasSeenWelcome = useSelector((state: { featureTracking?: Map<string, unknown> }) => {
    if (!state.featureTracking || !Map.isMap(state.featureTracking)) return false
    return Boolean(state.featureTracking.getIn(['data', 'communityMyLibraryWelcomeMessage']))
  })
  const [welcomeDismissedSession, setWelcomeDismissedSession] = useState(false)
  const showWelcome = isLoggedIn && !welcomeDismissedSession && !hasSeenWelcome

  const handleWelcomeDismiss = useCallback((permanent: boolean) => {
    setWelcomeDismissedSession(true)
    if (permanent) {
      actions.featureTracking.setFeatureTrackingDataPersistent({
        data: Map({
          communityMyLibraryWelcomeMessage: true,
        }),
      })
    }
  }, [actions])

  // ── Fetch profile (split across two parallel queries) ──────────────
  // Light query: lean field set (uid, userInfo, visibility flags) used to
  // unblock the page-level loading gate and feed the header/sidebar/tab
  // scaffold.  Settles fast (~hundreds of ms) so the user sees structure
  // immediately.
  //
  // cache-and-network: a return visit paints last known profile scalars
  // synchronously from the normalised UserProfile:<uuid> entity while the
  // background refetch validates.  UPDATE_PROFILE selects the same fields,
  // so cache writes stay correct without a manual refetch.
  const {
    data: profileLightData,
    loading: profileLightLoading,
  } = useQuery<GetCurrentProfileLightResponse>(GET_CURRENT_PROFILE_LIGHT, {
    variables: { language },
    skip: !isLoggedIn || !jwt,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  // In Progress carousel query — fetches *only* `userProfileByUid.inProgress`
  // (the same lean query the profile page uses).  The library page reads
  // nothing else from the full profile — every scalar comes from the light
  // query above — so there's no reason to pull the heavy
  // CoreUserProfileFields + UserProfileSocialFields payload here.
  // Doesn't gate the page render — its loading state surfaces inside the My
  // Playlists tab where the carousel lives; `useInProgressEnrichment`
  // tolerates `undefined` and renders an empty carousel until it lands.
  const contentLanguage = useMemo(() => [language], [language])
  const {
    data: inProgressData,
    loading: inProgressLoading,
  } = useQuery<GetProfileInProgressCarouselResponse>(GET_PROFILE_IN_PROGRESS_CAROUSEL, {
    variables: { uuid: authUuid, contentLanguage },
    skip: !isLoggedIn || !authUuid || !jwt,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  // Scalar profile fields come from the light query (already settled when
  // the page first renders).  `inProgress` comes from the carousel query and
  // is `undefined` until that lands.
  const profileLight = profileLightData?.userProfileCurrent
  const inProgress = inProgressData?.userProfileByUuid?.inProgress

  // ── Activity-flag mutation (Currently Watching / Watch History) ────
  // Reuses UPDATE_PROFILE; the response selects ...CoreUserProfileFields
  // which now includes both flags, so Apollo cache merges automatically.
  const [updateActivityVisibility] = useMutation<UpdateProfileResponse>(
    UPDATE_PROFILE,
    { context: { jwt } },
  )

  const handleActivityVisibility = useCallback(async (
    field: ActivityVisibilityField,
    vis: ActivityVisibilityValue,
  ) => {
    try {
      await updateActivityVisibility({
        variables: {
          input: { [field]: vis },
          language,
        },
      })
    } catch {
      // Stay in current state on failure (Community CLAUDE.md rule 8). Apollo
      // logs the error already; no toast surface here yet.
    }
  }, [updateActivityVisibility, language])

  // ── Fetch purchases (for Bonus/Purchases sidebar) ──────────────────
  const {
    data: purchasesData,
    loading: purchasesLoading,
    fetchMore: fetchMorePurchases,
  } = useQuery(GET_PURCHASES_LIST, {
    skip: !isLoggedIn || !jwt,
    variables: {
      language: [language],
      imageType: CONTENT_IMAGE_LIST,
      imageLanguage: [language, '_primary'],
      assetLanguage: language,
      learnerLevelLanguage: language,
    },
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  // ── Format purchases for sidebar + content views ────────────────────
  const {
    hasBonusVideos, hasBonusEBooks, hasPurchasesVideos, hasPurchasesEBooks,
    userBonuses, userPurchases,
  } = useMemo(() => {
    if (!purchasesData) {
      return {
        hasBonusVideos: false,
        hasBonusEBooks: false,
        hasPurchasesVideos: false,
        hasPurchasesEBooks: false,
        userBonuses: null,
        userPurchases: null,
      }
    }
    const purchasesList = _get(purchasesData, ['ecommerceSkuMappingsList', 'ecommerceSkuMappings'], {}) || {}
    const formatted = getUserPurchases(purchasesList)
    return {
      hasBonusVideos: formatted.bonus.contentList.size > 0,
      hasBonusEBooks: formatted.bonus.assetsList.size > 0,
      hasPurchasesVideos: formatted.purchases.contentList.size > 0,
      hasPurchasesEBooks: formatted.purchases.assetsList.size > 0,
      userBonuses: formatted.bonus,
      userPurchases: formatted.purchases,
    }
  }, [purchasesData])

  const purchasesCountTotal = _get(purchasesData, ['ecommerceSkuMappingsList', 'count'], 0)

  // The page's critical on-load queries (light profile / in-progress carousel /
  // purchases).  Passed to useCollectionsData so the heavy followed-playlists
  // items query waits for these (plus GET_MY_PLAYLISTS / GET_USER_HISTORY) to
  // finish before firing — see useCollectionsData.
  //
  // Under cache-and-network, `loading` flips true again on every revisit
  // while the background refetch runs.  We want `pageQueriesSettled` to
  // be true as soon as the data is *available* (cached or fresh), so the
  // dependent latch fires on the cache paint instead of waiting for the
  // network revalidation to land.
  const profileLightReady = !profileLightLoading || !!profileLightData
  const purchasesReady = !purchasesLoading || !!purchasesData
  const inProgressReady = !inProgressLoading || !!inProgressData
  const pageQueriesSettled = profileLightReady && purchasesReady && inProgressReady

  // ── Collections data (playlist tabs) ───────────────────────────────
  // Deep-link support: `/playlist/my-library?tab=discover` (or `?tab=following`)
  // lands on that tab. Validated against the known tab set so an unknown value
  // falls through to the default (My Playlists). Read once and threaded as the
  // hook's `initialTab` seed.
  const initialTab = useMemo<CollectionsTab | undefined>(() => {
    const requested = parseQuery(location.search || '').tab
    const validTabs = Object.values(TABS) as string[]
    return typeof requested === 'string' && validTabs.includes(requested)
      ? (requested as CollectionsTab)
      : undefined
  }, [location.search])

  // Scalar identity + visibility fields come from the light query; the
  // inProgress array comes from the carousel query (undefined until it lands).
  const collectionsData = useCollectionsData({
    uid,
    uuid: authUuid,
    language,
    jwt,
    isLoggedIn,
    dispatch,
    staticText,
    history,
    initialTab,
    authorUsername: profileLight?.displayName || 'Friend of Gaia',
    authorUid: uid,
    profileVisibility: profileLight?.profileVisibility || 'public',
    inProgress,
    // Thread `GET_PROFILE_IN_PROGRESS_CAROUSEL.loading` down so
    // `useCollectionsData` can OR it with `useInProgressEnrichment`'s
    // internal title-group hydration. The combined `inProgressIsHydrating`
    // drives the Currently Watching skeleton path in `ProfileMyPlaylists`
    // — without this the row would pop in once the carousel data lands.
    //
    // Pass the "no data yet" form (loading && !data) so revisits with a
    // cache hit do not re-trigger the skeleton state during the
    // background refetch.
    inProgressLoading: inProgressLoading && !inProgressData,
    queriesEnabled: communityEnabled,
    pageQueriesSettled,
    // Activity-flag visibility (CO-488).  Threaded through so the
    // privacy-summary "Sharing N" count reflects Currently Watching and
    // Watch History when they're set to public.
    //
    // `profileLight` types these as `ActivityVisibilityValue` (the raw
    // 'public' | 'private' literal union derived from the GraphQL string
    // enum). `useCollectionsData` declares them as the nominal
    // `FieldVisibility` enum (same runtime values, but TS treats the enum
    // as opaque). Cast at the boundary — values are identical, only the
    // nominal type differs.
    currentlyWatchingVisibility:
      (profileLight?.currentlyWatchingVisibility ?? null) as FieldVisibility | null,
    watchHistoryVisibility:
      (profileLight?.watchHistoryVisibility ?? null) as FieldVisibility | null,
  })

  // Welcome modal → "Discover" link (plain left-click). Soft in-page switch:
  // dismiss the modal for the session and switch to the Discover tab. We do
  // NOT push `?tab=discover` to the URL — persisting it means a later reload
  // (while the first-run modal is still eligible) would boot onto Discover
  // *under* the modal, so closing the modal would reveal Discover instead of
  // staying on My Library. Genuine deep-links (right-click → new tab, paste,
  // share) still work via the anchor's href + the page's `?tab=` reading.
  const { onCollectionsTabChange } = collectionsData
  const handleWelcomeDiscoverClick = useCallback(() => {
    handleWelcomeDismiss(false)
    onCollectionsTabChange(TABS.DISCOVER)
  }, [handleWelcomeDismiss, onCollectionsTabChange])

  const watchHistoryDeepLinkScrolled = useRef(false)

  // ── Page-view tracking (PostHog session-recording trigger) ─────────────
  // Fires once per mount after the light profile query settles.  The ref
  // guard prevents double-fires under React strict-mode and Apollo cache
  // refetches.
  const trackLibraryViewed = usePostHogEvent('community_my_library_viewed')
  const hasTrackedLibraryView = useRef(false)
  useEffect(() => {
    if (hasTrackedLibraryView.current) return
    // cache-and-network: fire as soon as we have the profile (cached or
    // fresh) so revisits track immediately rather than waiting for the
    // background refetch to settle.
    if (profileLightLoading && !profileLightData) return
    if (!isLoggedIn) return
    if (!communityEnabled) return
    hasTrackedLibraryView.current = true
    // PostHog naming convention is snake_case; map the in-app camelCase
    // collectionsTab ('myPlaylists' | 'following' | 'discover') to the
    // snake_case CommunityLibraryTab enum.
    let activeTab: CommunityLibraryTab
    if (collectionsData.collectionsTab === 'myPlaylists') activeTab = 'my_playlists'
    else if (collectionsData.collectionsTab === 'following') activeTab = 'following'
    else activeTab = 'discover'
    trackLibraryViewed(activeTab)
  }, [
    profileLightLoading,
    profileLightData,
    isLoggedIn,
    communityEnabled,
    collectionsData.collectionsTab,
    trackLibraryViewed,
  ])

  // Back-compat: /playlist/watch-history used to open the dedicated watch-history view.
  // Community My Library embeds that section; scroll to it once content can render.
  // Gate solely on `playlistsLoading` (the slice's "no data yet" signal,
  // defined as `shellLoading && !playlistsRawData` inside useProfilePlaylists).
  // Combining with `playlistsRawData` here would force this effect to re-fire
  // on every Apollo cache broadcast (the response wrapper recomputes even
  // when the inner array is structurally identical).  Under cache-and-network,
  // a return visit hits the cache, `playlistsLoading` is false immediately,
  // and the scroll runs without waiting for the background revalidation.
  useEffect(() => {
    if (location.pathname !== URL_PLAYLIST_WATCH_HISTORY) {
      watchHistoryDeepLinkScrolled.current = false
      return
    }
    if (collectionsData.myPlaylists.playlistsLoading) {
      return
    }
    if (watchHistoryDeepLinkScrolled.current) {
      return
    }
    watchHistoryDeepLinkScrolled.current = true
    scrollToPlaylistSection('system-playlist-watch-history')
  }, [
    location.pathname,
    collectionsData.myPlaylists.playlistsLoading,
  ])

  // ── Translated UI copy ─────────────────────────────────────────────
  // Resolved via the surrounding <StaticTextProvider> set up by the Outer
  // wrapper below. Declared before any conditional gates so hook order is
  // stable across renders.
  const videosTitle = useTranslatedText({ keys: ['videos'] })
  const eBooksTitle = useTranslatedText({ keys: ['eBooks'] })

  // ── Fallback to old library when community is disabled ─────────────
  if (!communityEnabled) {
    return <MyLibraryPage history={history} location={location} />
  }

  // ── Loading state ──────────────────────────────────────────────────
  // Gate on the lightweight profile query only — the heavy GET_CURRENT_PROFILE
  // can land seconds later because of inProgress.contentFeatured nesting.
  // The Currently Watching carousel renders its own skeleton until the heavy
  // query lands.
  if (!isLoggedIn || (profileLightLoading && !profileLightData)) {
    return (
      <div className="community-my-library community-my-library--loading">
        <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
      </div>
    )
  }

  const renderContentView = () => {
    const { pathname } = location
    if (pathname === URL_PLAYLIST_BONUS_VIDEOS && userBonuses) {
      return (
        <MyLibraryPurchasesVideos
          pageState={{ name: videosTitle }}
          contentList={userBonuses.contentList}
          fetchMore={fetchMorePurchases}
          loading={purchasesLoading}
          count={0}
          totalCount={purchasesCountTotal}
        />
      )
    }
    if (pathname === URL_PLAYLIST_BONUS_EBOOKS && userBonuses) {
      return (
        <MyLibraryPurchasesEBooks
          pageState={{ name: eBooksTitle }}
          assetsList={userBonuses.assetsList}
          fetchMore={fetchMorePurchases}
          loading={purchasesLoading}
          count={0}
          totalCount={purchasesCountTotal}
        />
      )
    }
    if (pathname === URL_PLAYLIST_PURCHASES_VIDEOS && userPurchases) {
      return (
        <MyLibraryPurchasesVideos
          pageState={{ name: videosTitle }}
          contentList={userPurchases.contentList}
          fetchMore={fetchMorePurchases}
          loading={purchasesLoading}
          count={0}
          totalCount={purchasesCountTotal}
        />
      )
    }
    if (pathname === URL_PLAYLIST_PURCHASES_EBOOKS && userPurchases) {
      return (
        <MyLibraryPurchasesEBooks
          pageState={{ name: eBooksTitle }}
          assetsList={userPurchases.assetsList}
          fetchMore={fetchMorePurchases}
          loading={purchasesLoading}
          count={0}
          totalCount={purchasesCountTotal}
        />
      )
    }
    return (
      <div className="community-my-library-content">
        <PlaylistTabContainer
          {...collectionsData}
          watchHistoryVisibility={profileLight?.watchHistoryVisibility ?? null}
          currentlyWatchingVisibility={profileLight?.currentlyWatchingVisibility ?? null}
          onActivityVisibility={handleActivityVisibility}
          loggedInUuid={authUuid}
        />
      </div>
    )
  }

  return (
    <div className="community-my-library">
      <aside className="community-my-library__sidebar">
        <CommunityMyLibraryNav
          history={history}
          location={location}
          activeTab={collectionsData.collectionsTab}
          onTabChange={collectionsData.onCollectionsTabChange}
          hasBonusVideos={hasBonusVideos}
          hasBonusEBooks={hasBonusEBooks}
          hasPurchasesVideos={hasPurchasesVideos}
          hasPurchasesEBooks={hasPurchasesEBooks}
        />
      </aside>
      <main className="community-my-library__main">
        {renderContentView()}
      </main>
      {showWelcome && (
        <Suspense fallback={null}>
          <CommunityMyLibraryWelcome
            onDismiss={handleWelcomeDismiss}
            loggedInUuid={authUuid}
            onDiscoverClick={handleWelcomeDiscoverClick}
          />
        </Suspense>
      )}
    </div>
  )
}

// Custom memo comparator: only compare the props this component actually uses.
// connectPage injects auth, resolver, page, user as extra props whose Immutable
// Map references change on unrelated Redux dispatches — ignore them.
function propsAreEqual (
  prev: CommunityMyLibraryPageProps,
  next: CommunityMyLibraryPageProps,
): boolean {
  return (
    prev.history === next.history
    && prev.location === next.location
    && prev.staticText === next.staticText
  )
}

// Outer wrapper: receives `staticText` from connectStaticText and provides
// it to the subtree so descendants' `useTranslatedText` hooks resolve against
// the `communityMyLibraryPage` slice. Inner still receives `staticText`
// because `useCollectionsData` reads `seeMore` / `showLess` labels via
// `.getIn` — that hook has not yet migrated to `useTranslatedText`.
// (Child slices `profileCollectionsTabs`, `profileMyPlaylists` own their
// own connectStaticText HOCs, so no `staticText` prop is threaded into them.)
function CommunityMyLibraryPageOuter (props: CommunityMyLibraryPageProps) {
  return (
    <MobileBreakpointProvider breakpoints={COMMUNITY_BREAKPOINTS}>
      <StaticTextProvider staticText={props.staticText}>
        <CommunityMyLibraryPageInner {...props} />
      </StaticTextProvider>
    </MobileBreakpointProvider>
  )
}

const CommunityMyLibraryPage = memo(CommunityMyLibraryPageOuter, propsAreEqual)

export default compose(
  connectPage({
    seoType: PAGE_SEO_TYPE_LOCATION,
    storeBranch: '',
    storeKey: 'communityMyLibraryPage',
  }),
  connectStaticText({ storeKey: 'communityMyLibraryPage' }),
  withCommunityErrorBoundary,
)(CommunityMyLibraryPage)
